/**
 * Campaign Service - API calls for Campaign Management
 * 
 * Handles all campaign-related API operations:
 * - List campaigns with filtering and pagination
 * - Create new campaigns
 * - Update existing campaigns
 * - Delete campaigns
 * - Activate/Deactivate campaigns
 */

import { PROMOS_ENDPOINTS } from './endpoints';
import secureApi from '../../utils/secureApi';
import {
  Campaign,
  CampaignListResponse,
  CampaignListParams,
  CreateCampaignRequest,
  CampaignFormData,
  CampaignStatus,
} from './types/campaignTypes';

/**
 * Transform API response to Campaign interface
 * Maps _id to id, totalUsages to totalUsage, revenueCollected to revenueImpact, etc.
 */
const transformCampaignResponse = (item: any): Campaign => {
  return {
    id: item._id || item.id,
    name: item.name,
    description: item.description,
    campaignType: item.campaignType,
    status: item.status,
    startAt: item.startAt,
    endAt: item.endAt,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    activePromos: item.activePromos || 0,
    totalUsage: item.totalUsages || item.totalUsage || 0,
    revenueImpact: item.revenueCollected || item.revenueImpact || 0,
    revenueUplift: item.revenueUplift || '+0%',
    funding: item.funding,
    linkedPromos: item.linkedPromos || [],
    linkedPromoDetails: item.linkedPromoDetails,
  };
};

/**
 * Get all campaigns with filtering and pagination
 */
export const getCampaigns = async (params: CampaignListParams = {}): Promise<CampaignListResponse> => {
  try {
    const response = await secureApi.get(PROMOS_ENDPOINTS.CAMPAIGNS.LIST_ALL, {
      params: {
        status: params.status,
        campaignType: params.campaignType,
        pageNum: params.pageNum || 1,
        pageSize: params.pageSize || 10,
        search: params.search,
        sortBy: params.sortBy || 'createdAt',
        sortOrder: params.sortOrder || 'desc',
        startAt: params.startAt,
        endAt: params.endAt,
      },
    });
    
    // Transform API response to match our expected structure
    // API returns: { data: { paginatedResults: [...], totalCount: 1 } }
    // We expect: { campaigns: [...], totalCount: 1, currentPage: 1, totalPages: 1, pageSize: 10 }
    const apiData = response.data?.data || response.data;
    const paginatedResults = apiData.paginatedResults || apiData.campaigns || [];
    const totalCount = apiData.totalCount || 0;
    const currentPage = params.pageNum || 1;
    const pageSize = params.pageSize || 10;
    const totalPages = Math.ceil(totalCount / pageSize);
    
    // Transform campaigns: map _id to id, totalUsages to totalUsage, etc.
    const campaigns: Campaign[] = paginatedResults.map((item: any) => transformCampaignResponse(item));
    
    return {
      campaigns,
      totalCount,
      currentPage,
      totalPages,
      pageSize,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get campaign by ID
 */
export const getCampaignById = async (id: string): Promise<Campaign> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.CAMPAIGNS.GET_BY_ID.replace('{id}', id);
    const response = await secureApi.get(endpoint);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformCampaignResponse(item);
  } catch (error) {
    throw error;
  }
};

/**
 * Create new campaign
 * Only sends required fields: name, description, campaignType, startAt, endAt
 * Status is added based on action (ACTIVE for activate, DRAFT for draft)
 */
export const createCampaign = async (campaignData: CampaignFormData, status: 'ACTIVE' | 'DRAFT' = 'ACTIVE'): Promise<Campaign> => {
  try {
    // Only send required fields as per API specification, plus status
    const payload = {
      name: campaignData.name,
      description: campaignData.description,
      campaignType: campaignData.campaignType,
      startAt: campaignData.startAt,
      endAt: campaignData.endAt,
      status: status,
    };
    
    const response = await secureApi.post(PROMOS_ENDPOINTS.CAMPAIGNS.CREATE, payload);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformCampaignResponse(item);
  } catch (error: any) {
    // Extract error message from API response
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to create campaign. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update existing campaign content
 * Only sends content fields: name, description, campaignType, startAt, endAt
 * Does NOT include status - use updateCampaignStatus for status changes
 */
export const updateCampaign = async (id: string, campaignData: CampaignFormData): Promise<Campaign> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.CAMPAIGNS.UPDATE.replace('{id}', id);
    // Only send content fields as per API specification
    const payload = {
      name: campaignData.name,
      description: campaignData.description,
      campaignType: campaignData.campaignType,
      startAt: campaignData.startAt,
      endAt: campaignData.endAt,
    };
    
    const response = await secureApi.post(endpoint, payload);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformCampaignResponse(item);
  } catch (error: any) {
    // Extract error message from API response
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to update campaign. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update campaign status
 */
export const updateCampaignStatus = async (id: string, status: CampaignStatus): Promise<Campaign> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.CAMPAIGNS.UPDATE_STATUS.replace('{id}', id);
    const payload = {
      status: status,
    };
    
    const response = await secureApi.post(endpoint, payload);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformCampaignResponse(item);
  } catch (error: any) {
    // Extract error message from API response
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to update campaign status. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete campaign
 */
export const deleteCampaign = async (id: string): Promise<void> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.CAMPAIGNS.DELETE.replace('{id}', id);
    await secureApi.delete(endpoint);
  } catch (error) {
    throw error;
  }
};

/**
 * Activate campaign
 */
export const activateCampaign = async (id: string): Promise<Campaign> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.CAMPAIGNS.ACTIVATE.replace('{id}', id);
    const response = await secureApi.patch(endpoint);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformCampaignResponse(item);
  } catch (error) {
    throw error;
  }
};

/**
 * Deactivate campaign
 */
export const deactivateCampaign = async (id: string): Promise<Campaign> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.CAMPAIGNS.DEACTIVATE.replace('{id}', id);
    const response = await secureApi.patch(endpoint);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformCampaignResponse(item);
  } catch (error) {
    throw error;
  }
};

/**
 * Save campaign as draft (create with DRAFT status)
 * Only sends required fields: name, description, campaignType, startAt, endAt
 * Status is set to DRAFT
 */
export const saveCampaignAsDraft = async (campaignData: CampaignFormData): Promise<Campaign> => {
  try {
    // Use createCampaign with DRAFT status
    return await createCampaign(campaignData, 'DRAFT');
  } catch (error: any) {
    throw error;
  }
};
